// Clause-quote card for the KPI detail drawer. Renders `kpi.definition`
// as a blockquote with a left-border + italic treatment, tagged with
// the clause reference. "View in contract" stub navigates back to the
// contract — wiring lives outside this component (caller passes the
// handler).
//
// Clause reference reads `kpi.kpiClauseReference` first (the new
// always-visible field), falling back to the legacy
// `slaClauseReference` so legacy KPIs still display. Any new KPI
// ships with ONLY the new field set.

import type { JSX } from "react";
import type { Kpi } from "../../../../../types";

export interface KpiMeasurementCriteriaProps {
  kpi: Kpi;
  onViewInContract?: () => void;
}

export default function KpiMeasurementCriteria({
  kpi,
  onViewInContract,
}: KpiMeasurementCriteriaProps): JSX.Element {
  const definition = kpi.definition?.trim() ?? "";
  const reference =
    (kpi.kpiClauseReference ?? kpi.slaClauseReference)?.trim() ?? "";

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <h3 className="mb-2 text-h4 text-slate-900 dark:text-slate-100">
        Measurement criteria
      </h3>
      {definition ? (
        <blockquote className="border-l-2 border-brand-500 bg-slate-50 px-4 py-3 text-body italic text-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
          {definition}
        </blockquote>
      ) : (
        <p className="text-body-sm text-slate-500 dark:text-slate-400">
          No measurement criteria recorded.
        </p>
      )}
      {reference && (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-caption text-slate-500 dark:text-slate-400">
          <span>{reference}</span>
          <button
            type="button"
            onClick={onViewInContract}
            className="rounded-sm text-brand-600 hover:underline focus:outline-hidden focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 dark:text-brand-400 dark:focus:ring-offset-slate-800"
          >
            View in contract
          </button>
        </p>
      )}
    </div>
  );
}
